import { apiClient } from './api.service';
import type { TransformationOrder } from '@/types/transformation.types';

export interface CreateTransformationOrderData {
  transformationRuleId: string;
  locationId: string;
  sourceQuantity?: number; // Optional for backward compatibility
  targetQuantity?: number; // Optional for backward compatibility
  sourceItems?: Array<{
    ingredientId: string;
    requestedQuantity: number;
    unit: string;
  }>;
  targetItems?: Array<{
    ingredientId: string;
    expectedQuantity: number;
    unit: string;
  }>;
  notes?: string;
  scheduledDate?: string;
}

export interface UpdateTransformationOrderData {
  status?: TransformationOrder['status'];
  actualSourceQuantity?: number;
  actualTargetQuantity?: number;
  efficiency?: number;
  notes?: string;
  completedAt?: string;
}

export interface TransformationOrdersResponse {
  success: boolean;
  data: TransformationOrder[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  error?: string;
}

export interface SingleTransformationOrderResponse {
  success: boolean;
  data: TransformationOrder;
  error?: string;
  message?: string;
}

class TransformationOrdersService {
  private baseUrl = '/transformation-orders';

  async getTransformationOrders(params: {
    search?: string;
    locationId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  } = {}): Promise<TransformationOrdersResponse> {
    try {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const url = `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get<TransformationOrdersResponse>(url);

      return response;
    } catch (error) {
      console.error('Error fetching transformation orders:', error);
      throw new Error('Error al obtener las órdenes de transformación');
    }
  }

  async getTransformationOrder(id: string): Promise<SingleTransformationOrderResponse> {
    try {
      const response = await apiClient.get<SingleTransformationOrderResponse>(`${this.baseUrl}/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching transformation order:', error);
      throw new Error('Error al obtener la orden de transformación');
    }
  }

  async createTransformationOrder(data: CreateTransformationOrderData): Promise<SingleTransformationOrderResponse> {
    try {
      const response = await apiClient.post<SingleTransformationOrderResponse>(this.baseUrl, data);
      return response;
    } catch (error) {
      console.error('Error creating transformation order:', error);
      throw new Error('Error al crear la orden de transformación');
    }
  }

  async updateTransformationOrder(id: string, data: UpdateTransformationOrderData): Promise<SingleTransformationOrderResponse> {
    try {
      const response = await apiClient.put<SingleTransformationOrderResponse>(`${this.baseUrl}/${id}`, data);
      return response;
    } catch (error) {
      console.error('Error updating transformation order:', error);
      throw new Error('Error al actualizar la orden de transformación');
    }
  }

  async deleteTransformationOrder(id: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; message?: string; error?: string }>(`${this.baseUrl}/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting transformation order:', error);
      throw new Error('Error al eliminar la orden de transformación');
    }
  }

  async executeTransformation(id: string, data: {
    actualTargetQuantity: number;
    notes?: string;
  }): Promise<SingleTransformationOrderResponse> {
    try {
      const response = await apiClient.put<SingleTransformationOrderResponse>(`${this.baseUrl}/${id}/execute`, data);
      return response;
    } catch (error) {
      console.error('Error executing transformation:', error);
      throw new Error('Error al ejecutar la transformación');
    }
  }

  async cancelTransformation(id: string, data: { reason?: string }): Promise<SingleTransformationOrderResponse> {
    try {
      const response = await apiClient.put<SingleTransformationOrderResponse>(`${this.baseUrl}/${id}/cancel`, data);
      return response;
    } catch (error) {
      console.error('Error cancelling transformation:', error);
      throw new Error('Error al cancelar la transformación');
    }
  }
}

export const transformationOrdersService = new TransformationOrdersService();